import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Collapse,
  Divider,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  type Theme,
  Toolbar,
  Tooltip,
} from '@mui/material';
import { useCallback, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useAuth } from '@/auth/useAuth';

import type { NavGroup, NavItem } from './NavItem';

const SIDE_NAV_WIDTH = 260;

/**
 * The icon-rail width. Wide enough for a 24px icon inside a comfortable hit target and the active
 * item's leading accent bar, narrow enough that collapsing is visibly worth doing.
 */
const SIDE_NAV_COLLAPSED_WIDTH = 72;

/** localStorage key for the persisted expanded/collapsed state of grouped navs. */
export const NAV_GROUPS_STORAGE_KEY = 'willnq.nav.expanded-groups';

export interface SideNavProps {
  items: readonly NavItem[];
  /** Collapsible sections rendered below the ungrouped items (Platform Admin nav). */
  groups?: readonly NavGroup[];
  open: boolean;
  onClose: () => void;
  /** Permanent on desktop (persistent drawer), temporary/overlay on mobile. */
  variant: 'permanent' | 'temporary';
  /**
   * Render as an icon rail instead of the full-width nav. Honoured only by the `permanent`
   * (desktop) drawer — the mobile drawer is a transient overlay that is already dismissible, so
   * shrinking it would trade readable labels for nothing.
   */
  collapsed?: boolean;
}

function readStoredExpansion(): Record<string, boolean> {
  try {
    const raw = window.localStorage.getItem(NAV_GROUPS_STORAGE_KEY);
    return raw ? (JSON.parse(raw) as Record<string, boolean>) : {};
  } catch {
    return {}; // corrupted storage or privacy mode — fall back to defaults, never crash the nav
  }
}

/**
 * The generic side navigation shell. Persona layouts supply `items` (and optionally
 * `groups`); this component owns only rendering, role-filtering, active-route
 * highlighting, and collapse persistence — never persona-specific copy. Groups default to
 * expanded; a group holding the active route always opens so the current page is never hidden.
 */
export function SideNav({
  items,
  groups = [],
  open,
  onClose,
  variant,
  collapsed = false,
}: SideNavProps) {
  const { hasAnyRole } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [expansion, setExpansion] = useState<Record<string, boolean>>(readStoredExpansion);

  // Only the desktop drawer collapses; see the `collapsed` prop doc.
  const isRail = collapsed && variant === 'permanent';

  const visibleItems = items.filter((item) => !item.roles || hasAnyRole(item.roles));
  const visibleGroups = groups
    .map((group) => ({
      ...group,
      items: group.items.filter((item) => !item.roles || hasAnyRole(item.roles)),
    }))
    .filter((group) => group.items.length > 0);

  // Longest-prefix match: nested routes (e.g. `/candidate/profile/resume`) share a prefix with
  // ancestor nav items (`/candidate/profile`, `/candidate`), so a simple `startsWith` per item would
  // highlight all of them at once. Only the single most specific match should ever be active.
  const allItems = useMemo(
    () => [...visibleItems, ...visibleGroups.flatMap((g) => g.items)],
    // eslint-disable-next-line react-hooks/exhaustive-deps -- derived from props each render; identity churn is fine here
    [items, groups, location.pathname],
  );
  const activeItem = allItems.reduce<NavItem | null>((best, item) => {
    const matches = location.pathname === item.to || location.pathname.startsWith(`${item.to}/`);
    if (!matches) return best;
    if (!best || item.to.length > best.to.length) return item;
    return best;
  }, null);

  const toggleGroup = useCallback((key: string) => {
    setExpansion((prev) => {
      const next = { ...prev, [key]: !(prev[key] ?? true) };
      try {
        window.localStorage.setItem(NAV_GROUPS_STORAGE_KEY, JSON.stringify(next));
      } catch {
        // storage unavailable — the session still works, expansion just won't persist
      }
      return next;
    });
  }, []);

  const isExpanded = (group: NavGroup) => {
    // The active page's group is always open — a collapsed group must never hide "where am I".
    if (group.items.some((item) => item === activeItem)) return true;
    return expansion[group.key] ?? true;
  };

  const itemButtonSx = {
    borderRadius: 1.5,
    mb: 0.5,
    position: 'relative',
    '&.Mui-selected': {
      color: 'primary.main',
      '& .MuiListItemIcon-root': { color: 'primary.main' },
    },
    // Leading accent bar so the active item is never conveyed by color alone (§7.9).
    '&.Mui-selected::before': {
      content: '""',
      position: 'absolute',
      left: 4,
      top: '22%',
      bottom: '22%',
      width: 3,
      borderRadius: 3,
      bgcolor: 'primary.main',
    },
  } as const;

  const renderItem = (item: NavItem, indented = false) => {
    const isActive = item === activeItem;
    const button = (
      <ListItemButton
        key={item.to}
        selected={isActive}
        onClick={() => {
          navigate(item.to);
          if (variant === 'temporary') onClose();
        }}
        // With the label hidden the icon is the only content, so the label has to reach the
        // accessibility tree some other way. The Tooltip alone is not enough — it is not announced
        // to a screen reader that never hovers.
        aria-label={isRail ? item.label : undefined}
        sx={{
          ...itemButtonSx,
          ...(indented && !isRail ? { pl: 2.5 } : null),
          ...(isRail ? { justifyContent: 'center', px: 1.5 } : null),
        }}
      >
        <ListItemIcon sx={{ minWidth: isRail ? 0 : 36, justifyContent: 'center' }}>
          {item.icon}
        </ListItemIcon>
        {isRail ? null : (
          <ListItemText
            primary={item.label}
            primaryTypographyProps={{ fontWeight: isActive ? 600 : 500 }}
          />
        )}
      </ListItemButton>
    );

    if (!isRail) return button;
    return (
      <Tooltip key={item.to} title={item.label} placement="right">
        {button}
      </Tooltip>
    );
  };

  const width = isRail ? SIDE_NAV_COLLAPSED_WIDTH : SIDE_NAV_WIDTH;

  /*
    Sit UNDER the app bar, not over it.

    MUI stacks every drawer at `zIndex.drawer` (1200), above `zIndex.appBar` (1100), so the
    permanent drawer painted over the left 260px of the fixed toolbar and swallowed whatever was
    placed there — the brand lockup, and now the collapse toggle. The `<Toolbar />` spacer below
    already assumes a CLIPPED drawer; only the stacking order was missing.

    Demoting this drawer is the narrow fix. Promoting the app bar instead would lift it above every
    OTHER drawer too — the mobile nav and the notification panel are both modal drawers at the same
    1200 — leaving a live toolbar floating over their scrims.
  */
  const dockedBelowAppBar =
    variant === 'permanent'
      ? { zIndex: (theme: Theme) => theme.zIndex.appBar - 1 }
      : null;

  return (
    <Drawer
      variant={variant}
      open={variant === 'permanent' ? true : open}
      onClose={onClose}
      ModalProps={{ keepMounted: true }}
      sx={{
        width,
        flexShrink: 0,
        ...dockedBelowAppBar,
        // Animate BOTH the drawer root and its paper. The root reserves the space in the shell's
        // flex row and the paper is `position: fixed`, so animating only one makes the content
        // slide while the rail jumps, or the reverse.
        transition: (theme) =>
          theme.transitions.create('width', { duration: theme.transitions.duration.shorter }),
        '& .MuiDrawer-paper': {
          width,
          boxSizing: 'border-box',
          // MUI sets the 1200 on the paper as well as the root, so both need demoting.
          ...dockedBelowAppBar,
          // Labels are still in the DOM mid-animation; without this they spill past the rail.
          overflowX: 'hidden',
          transition: (theme) =>
            theme.transitions.create('width', { duration: theme.transitions.duration.shorter }),
        },
      }}
    >
      <Toolbar />
      <Divider />
      <List sx={{ px: 1, py: 1 }}>
        {visibleItems.map((item) => renderItem(item))}

        {visibleGroups.map((group) => {
          // A rail has no room for a section header, and an expand/collapse control inside an
          // already-collapsed nav is a dead end. Group members become plain rail icons behind a
          // divider instead — every destination stays one click away, which is the whole point.
          if (isRail) {
            return (
              <li key={group.key}>
                <Divider sx={{ my: 1 }} aria-hidden />
                <List disablePadding aria-label={group.label}>
                  {group.items.map((item) => renderItem(item))}
                </List>
              </li>
            );
          }

          const expanded = isExpanded(group);
          return (
            <li key={group.key}>
              <List disablePadding sx={{ mt: 0.5 }}>
                <ListItemButton
                  onClick={() => toggleGroup(group.key)}
                  aria-expanded={expanded}
                  sx={{ borderRadius: 1.5, py: 0.5 }}
                >
                  <ListSubheader
                    component="span"
                    disableSticky
                    sx={{
                      p: 0,
                      flexGrow: 1,
                      lineHeight: 2,
                      bgcolor: 'transparent',
                      fontSize: 11,
                      fontWeight: 700,
                      letterSpacing: '0.08em',
                      textTransform: 'uppercase',
                    }}
                  >
                    {group.label}
                  </ListSubheader>
                  {expanded ? (
                    <ExpandLessIcon fontSize="small" color="disabled" />
                  ) : (
                    <ExpandMoreIcon fontSize="small" color="disabled" />
                  )}
                </ListItemButton>
                <Collapse in={expanded} timeout="auto" unmountOnExit={false}>
                  <List disablePadding>{group.items.map((item) => renderItem(item, true))}</List>
                </Collapse>
              </List>
            </li>
          );
        })}
      </List>
    </Drawer>
  );
}
